import React, { ReactNode } from 'react';
import Dialog from '@mui/material/Dialog';
import { Link } from 'react-router-dom';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { styled } from '@mui/material/styles';
import { Divider } from '@mui/material';
import { CloseIcon } from 'src/components/SvgIcons/SvgIcons';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { ExportCheckBox } from 'src/components/BeaconV2Components/common/AppExportModal/AppExportModal';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { hexToRgba, useStacklineTheme } from '@stackline/ui';

interface GenericStyledDialogProps {
  theme: any;
}

const GenericStyledDialog = styled(Dialog)<GenericStyledDialogProps>(({ theme }) => ({
  '& .MuiPaper-root': {
    width: '640px',
    height: '545px',
    boxShadow: `0 0 ${theme.spacing.md} 0 ${hexToRgba(theme.colors.primary, 0.4)}`,
    borderRadius: theme.spacing.sm
  }
}));

interface ExportModalProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}

export const ExportModalRoot: React.FC<ExportModalProps> & {
  Header: React.FC<ExportModalHeaderProps>;
  Body: React.FC<ExportModalBodyProps>;
  Footer: React.FC<ExportModalFooterProps>;
} = ({ open, onClose, children }) => {
  const theme = useStacklineTheme();

  return (
    <GenericStyledDialog open={open} onClose={onClose} theme={theme as any}>
      {children}
    </GenericStyledDialog>
  );
};

// Header Component
interface ExportModalHeaderProps {
  title: string;
  onClose: () => void;
}

const Header = ({ title, onClose }: ExportModalHeaderProps) => (
  <>
    <Flex height="72px" justifyContent="center" alignItems="center" paddingX="48px" paddingY="18px">
      <Text variant="h4">{title}</Text>
      <CloseIcon
        onClick={onClose}
        style={{ position: 'absolute', cursor: 'pointer', width: '24px', height: '24px', right: '24px' }}
      />
    </Flex>
    <Divider />
  </>
);

// Body Component
interface ExportModalBodyProps {
  children: ReactNode;
  isSeparatedByWeek?: boolean;
  isIncludeComparison?: boolean;
  setIsSeparatedByWeek?: (value: boolean) => void;
  setIsIncludeComparison?: (value: boolean) => void;
  customTimePeriodComponent?: ReactNode;
  hideCustomizeSection?: boolean;
}

const Body = ({
  children,
  isSeparatedByWeek = false,
  isIncludeComparison = false,
  setIsSeparatedByWeek = () => {},
  setIsIncludeComparison = () => {},
  customTimePeriodComponent,
  hideCustomizeSection = false
}: ExportModalBodyProps) => (
  <Flex flexDirection="column" paddingTop="23.5px" marginLeft="48px" height="400px">
    <Text variant="subtitle1" sx={{ height: '21px' }}>
      Export Data Structure
    </Text>
    <Flex marginTop="8px">
      <Text variant="body2">Select the level of granularity that you would like in the exported file.</Text>
    </Flex>
    <Flex marginTop="24px" alignItems="center">
      <Text sx={{ height: '19px' }} variant="subtitle2">
        Group by:
      </Text>
      <Flex marginLeft="8px">
        {/* Dropdown component */}
        {children}
      </Flex>
    </Flex>
    {!hideCustomizeSection && (
      <Flex marginTop="24px">
        <Text sx={{ height: '19px' }} variant="subtitle2">
          Customize
        </Text>
      </Flex>
    )}
    <Flex flexDirection="column" gap={3} marginTop="6.5px">
      {customTimePeriodComponent ?? (
        <>
          <ExportCheckBox
            isSelected={isSeparatedByWeek}
            id="separateDataByWeek"
            label="Separate data by week"
            onSelect={(e) => {
              setIsSeparatedByWeek(e.target.checked);
            }}
          />
          {/* Include comparison checkbox */}
          <ExportCheckBox
            isSelected={isIncludeComparison}
            id="includeComparison"
            label="Include comparison time period"
            onSelect={(e) => setIsIncludeComparison(e.target.checked)}
          />
        </>
      )}
    </Flex>
  </Flex>
);

// Footer Component
interface ExportModalFooterProps {
  searchParams: string;
  handleExportClick: () => void;
  exportButtonDisabled?: boolean;
  handleClose: () => void;
}

const Footer = ({ handleClose, searchParams, handleExportClick, exportButtonDisabled }: ExportModalFooterProps) => (
  <>
    <Divider />
    <Flex justifyContent="space-between" alignItems="center" paddingX="48px" paddingY="20px">
      <Link to={`/account/downloads${searchParams}`}>
        <SlButton sx={{ width: '142px' }} onClick={handleClose}>
          View downloads
        </SlButton>
      </Link>
      <SlButton
        buttonSx={{ width: '81px' }}
        variant="contained"
        onClick={handleExportClick}
        disabled={exportButtonDisabled}
      >
        Export
      </SlButton>
    </Flex>
  </>
);

ExportModalRoot.Header = Header;
ExportModalRoot.Body = Body;
ExportModalRoot.Footer = Footer;

export default ExportModalRoot;
